import React, { useCallback, useEffect, useState } from "react";
import AskAddyV2 from "../../models/askAddyV2";
import DocView, { AboutFile } from "./DocView";
import { toEditableDoc } from "./docText";

const savedSummary = ({ added = 0, changed = 0, forgotten = 0 }) => {
  const parts = [
    added && `${added} added`,
    changed && `${changed} changed`,
    forgotten && `${forgotten} forgotten`,
  ].filter(Boolean);
  return parts.length ? `Saved: ${parts.join(", ")}.` : "Saved.";
};

/** MEMORY.md: what Lendy knows about you, as one document you can read and edit, opened under its row. Only yours. */
const MemoryView = ({ identity, assistantName }) => {
  const [markdown, setMarkdown] = useState(null);
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [refused, setRefused] = useState([]);
  const [isBusy, setIsBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { markdown: loaded } = await AskAddyV2.memoryDoc.get(identity);
      setMarkdown(loaded);
      setError("");
    } catch (loadError) {
      setError(loadError.message);
    }
  }, [identity]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    setIsBusy(true);
    setError("");
    try {
      const result = await AskAddyV2.memoryDoc.save(draft, identity);
      setMarkdown(result.markdown);
      setRefused(result.refused || []);
      setNotice(savedSummary(result));
      setDraft(null);
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setIsBusy(false);
    }
  };

  return (
      <div className="lendy-profile-editor lendy-profile-editor--inline lendy-profile-doc">
        <AboutFile>What {assistantName} knows about you. Only you see it.</AboutFile>
        {markdown === null && !error && (
          <p className="lendy-profile-note">Loading…</p>
        )}
        {markdown !== null && draft === null && (
          <>
            <DocView markdown={markdown} emptyText="Nothing yet." />
            <div className="lendy-profile-editor-foot">
              <small>{notice}</small>
              <button
                type="button"
                className="lendy-pill-button lendy-pill-button--go"
                onClick={() => {
                  setDraft(toEditableDoc(markdown));
                  setNotice("");
                  setRefused([]);
                }}
              >
                Edit
              </button>
            </div>
          </>
        )}
        {draft !== null && (
          <>
            <textarea
              aria-label="Memory"
              value={draft}
              disabled={isBusy}
              onChange={(event) => setDraft(event.target.value)}
            />
            <div className="lendy-profile-editor-foot">
              <button
                type="button"
                className="lendy-btn lendy-btn--text"
                disabled={isBusy}
                onClick={() => setDraft(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="lendy-pill-button lendy-pill-button--go"
                disabled={isBusy || draft === toEditableDoc(markdown)}
                onClick={save}
              >
                {isBusy ? "Saving…" : "Save"}
              </button>
            </div>
          </>
        )}
        {refused.map((item) => (
          <p
            key={item.content}
            className="lendy-profile-note lendy-profile-note--error"
          >
            “{item.content}”: {item.reason}
          </p>
        ))}
        {error && (
          <p className="lendy-profile-note lendy-profile-note--error">
            {error}
          </p>
        )}
      </div>
  );
};

export default MemoryView;
